import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'
import { ZodError } from 'zod'

export interface ErrorWithSentryId extends Error {
  sentryEventId?: string
}

export class AppError extends Error {
  public readonly timestamp: Date
  public readonly context: Record<string, unknown>
  public sentryEventId?: string

  constructor(
    message: string,
    public readonly code: string,
    context: Record<string, unknown> = {}
  ) {
    super(message)
    this.name = this.constructor.name
    this.timestamp = new Date()
    this.context = context

    Error.captureStackTrace(this, this.constructor)
  }

  reportToSentry(level: Sentry.SeverityLevel = 'error'): string | undefined {
    if (process.env.REACT_SENTRY_ENVIRONMENT !== 'local') {
      const eventId = Sentry.captureException(this, {
        level,
        tags: {
          error_code: this.code,
          error_type: this.name,
        },
        contexts: {
          error_details: this.context,
        },
      })

      this.sentryEventId = eventId
      this.context.sentryEventId = eventId
      return eventId
    }
    return undefined
  }
}

export class ApiError extends AppError {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly endpoint: string,
    public readonly method: string,
    public readonly responseData?: unknown,
    public readonly requestData?: unknown
  ) {
    super(message, `API_${statusCode}`, {
      statusCode,
      endpoint,
      method,
      responseData,
      requestData,
    })
  }

  isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500
  }

  isServerError(): boolean {
    return this.statusCode >= 500
  }

  shouldRetry(): boolean {
    if (this.isClientError()) {
      return this.statusCode === 408 || this.statusCode === 429
    }
    return true
  }
}

export class AuthError extends AppError {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly endpoint: string
  ) {
    super(message, `AUTH_${statusCode}`, {
      statusCode,
      endpoint,
    })
  }
}

export class NetworkError extends AppError {
  constructor(
    message: string,
    public readonly endpoint: string,
    public readonly originalError?: Error
  ) {
    super(message, 'NETWORK_ERROR', {
      endpoint,
      originalError: originalError?.message,
    })
  }
}

export class ValidationError extends AppError {
  constructor(
    message: string,
    public readonly endpoint: string,
    public readonly zodError: ZodError,
    public readonly receivedData: unknown
  ) {
    super(message, 'VALIDATION_ERROR', {
      endpoint,
      zodIssues: zodError.issues,
      receivedData,
    })
  }
}
export function formatZodError(
  zodError: ZodError,
  endpoint: string = 'unknown'
): ValidationError {
  const issueGroups = new Map<string, number>()

  zodError.issues.forEach((issue) => {
    const path = issue.path.join('.')
    const fieldPath = path.replace(/\.\d+/g, '')
    const issueKey = `${fieldPath}: ${issue.message}`

    const count = issueGroups.get(issueKey) || 0
    issueGroups.set(issueKey, count + 1)
  })

  let message = 'Response validation failed'
  if (issueGroups.size > 0) {
    const issueSummary = Array.from(issueGroups.entries())
      .map(([issue, count]) =>
        count > 1 ? `${issue} (${count} items)` : issue
      )
      .join('\n')

    message = `${message}:\n${issueSummary}`
  }

  return new ValidationError(message, endpoint, zodError, undefined)
}

export function extractApiError(
  error: unknown,
  endpoint: string,
  method: string = 'GET',
  requestData?: unknown
): AppError {
  if (error instanceof AxiosError) {
    const response = error.response

    if (!response) {
      return new NetworkError(
        error.message || 'Network request failed',
        endpoint,
        error
      )
    }

    if (response.status === 401 || response.status === 403) {
      return new AuthError(
        response.data?.message || 'Authentication failed',
        response.status,
        endpoint
      )
    }

    const message =
      response.data?.message ||
      response.data?.error ||
      response.statusText ||
      'API request failed'

    return new ApiError(
      message,
      response.status,
      endpoint,
      method,
      response.data,
      requestData
    )
  }

  if (error instanceof ZodError) {
    return formatZodError(error, endpoint)
  }

  if (error instanceof Error) {
    return new AppError(error.message, 'UNKNOWN_ERROR', {
      originalError: error.stack,
    })
  }
  return new AppError('An unexpected error occurred', 'UNKNOWN_ERROR', {
    thrownValue: String(error),
  })
}
